/* eslint-disable @typescript-eslint/no-explicit-any */
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';
import { LayoutsModule } from './layouts/layouts.module';
import { ExtrapagesModule } from './extrapages/extrapages.module';
import { ErrorInterceptor } from './core/helpers/error.interceptor';
import { API_URL, BaseUrlInterceptor } from './core/interceptors/base-url-interceptor.interceptor';
import { NgxPermissionsModule } from 'ngx-permissions';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { StoreModule } from '@ngrx/store';
import { reducers } from '@app/core/store/reduceIndex';
import { EffectsModule } from '@ngrx/effects';
import * as fromEffects from '@app/core/store/effectsIndex';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { environment as env } from '@env/environment';
import { PermissionsService } from '@shared';
import { TokenService } from './core/services/token.service';
import { firstValueFrom } from 'rxjs';

export function createTranslateLoader(http: HttpClient): any {
  return new TranslateHttpLoader(http, `${env.publicUrl}assets/languages/`, '.json');
}

function loadPermissionsAtReload(
  permissionService: PermissionsService,
  toast: ToastrService,
  tokenService: TokenService,
) {
  return () => {
    if (
      !location.pathname.includes('/template') &&
      !location.pathname.includes('auth') &&
      !location.pathname.includes('business-portal') &&
      tokenService.getTokens().accessToken &&
      tokenService.getTokens().refreshToken
    ) {
      // defaultValue: a request the error interceptor ended because the session is over completes
      // empty (it has already shown its one message and gone to login) — nothing more to say here
      return firstValueFrom(permissionService.getAllPermissions(), { defaultValue: null })
        .then((response) => {
          if (!response) return;
          if (response.status) {
            permissionService.setPermissions(response.data);
          } else {
            toast.error(response.message);
          }
        })
        .catch((error) => {
          toast.error(error.message);
          console.error('Error loading permissions:', error);
        });
    }
    return Promise.resolve();
  };
}

export function tokenGetter() {
  return JSON.parse(localStorage.getItem(environment.tokenKey) ?? '{}').accessToken;
}
@NgModule({
  declarations: [AppComponent],
  imports: [
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    BrowserModule,
    AppRoutingModule,
    //LeafletModule,
    LayoutsModule,
    NgxPermissionsModule.forRoot(),
    NgbModule,
    HttpClientModule,
    ExtrapagesModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      maxOpened: 2,
    }),
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([
      fromEffects.PainterEffects,
      fromEffects.SpecializationsEffect,
      fromEffects.UserTypesEffect,
      fromEffects.WorkAreaEffects,
      fromEffects.ColorsEffect,
      fromEffects.CurrentUserEffects,
      fromEffects.AdminTypeEffects,
      fromEffects.CitiesEffect,
      fromEffects.PendingCountsEffects,
      fromEffects.DictionaryEffects,
    ]),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: loadPermissionsAtReload,
      multi: true,
      deps: [PermissionsService, ToastrService, TokenService],
    },
    { provide: API_URL, useValue: environment.apiUrl },
    { provide: HTTP_INTERCEPTORS, useClass: BaseUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
